import { useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useSession } from './hooks/useSession';
import { Sidebar } from './components/Sidebar';
import { TopBar } from './components/TopBar';
import { ScrollToTop } from './components/ScrollToTop';
import { Loading } from './components/Loading';
import { Login } from './pages/Login';
import { AdminApp } from './admin/AdminApp';
import { Dashboard } from './pages/Dashboard';
import { PathPage } from './pages/PathPage';
import { TrackPage } from './pages/TrackPage';
import { LessonPage } from './pages/LessonPage';
import { ChecklistsPage } from './pages/ChecklistsPage';
import { GraderPage } from './pages/GraderPage';
import { CalculatorPage } from './pages/CalculatorPage';
import { PhotosPage } from './pages/PhotosPage';
import { SearchResults } from './pages/SearchResults';

export function App() {
  const { vendor, admin, content, loading } = useSession();
  const [query, setQuery] = useState('');
  // Drawer state for the off-canvas rail below 900px; inert on desktop.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  if (loading) return <Loading full />;

  // Staff get the console, not the portal. Checked before the vendor branch because an
  // admin has no vendor session and would otherwise be bounced to the vendor sign-in —
  // which is what made a correct staff password look invalid.
  if (admin) return <AdminApp />;

  if (!vendor) {
    // Preserve where they were headed so a deep link survives the sign-in round trip.
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* There is one sign-in form now; keep the old staff URL working for bookmarks. */}
        <Route path="/admin/login" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  // Signed in but the curriculum has not landed yet.
  if (!content) return <Loading full label="Loading your curriculum" />;

  return (
    <div className="vu-app">
      <ScrollToTop />
      <Sidebar open={menuOpen} onNavigate={() => setMenuOpen(false)} />
      {menuOpen && (
        <button
          type="button"
          className="vu-scrim"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}
      <main className="vu-main">
        <TopBar query={query} onQuery={setQuery} onMenu={() => setMenuOpen(true)} />
        {query.trim() ? (
          <SearchResults query={query.trim()} />
        ) : (
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/path" element={<PathPage />} />
            <Route path="/track/:trackId" element={<TrackPage />} />
            <Route path="/track/:trackId/:lessonId" element={<LessonPage />} />
            <Route path="/checklists" element={<ChecklistsPage />} />
            <Route path="/tools/grader" element={<GraderPage />} />
            <Route path="/tools/calculator" element={<CalculatorPage />} />
            <Route path="/tools/photos" element={<PhotosPage />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            {/* A signed-in vendor is not staff; don't dead-end them on a staff form. */}
            <Route path="/admin/*" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}
